import { Check, X } from "lucide-react";
import { validatePasswordStrength } from "@/lib/passwordValidation";

interface PasswordStrengthIndicatorProps {
  password: string;
}

export const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  if (!password) return null;

  const strength = validatePasswordStrength(password);

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${strength.color}`}
            style={{ width: `${(strength.score / 5) * 100}%` }}
          />
        </div>
        <span className="text-xs font-medium text-muted-foreground">{strength.label}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
        <div className="flex items-center gap-1">
          {strength.checks.minLength ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <X className="h-3 w-3 text-muted-foreground" />
          )}
          <span className={strength.checks.minLength ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
            Minst 8 tecken
          </span>
        </div>

        <div className="flex items-center gap-1">
          {strength.checks.hasUpperCase ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <X className="h-3 w-3 text-muted-foreground" />
          )}
          <span className={strength.checks.hasUpperCase ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
            En stor bokstav
          </span>
        </div>

        <div className="flex items-center gap-1">
          {strength.checks.hasLowerCase ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <X className="h-3 w-3 text-muted-foreground" />
          )}
          <span className={strength.checks.hasLowerCase ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
            En liten bokstav
          </span>
        </div>

        <div className="flex items-center gap-1">
          {strength.checks.hasNumber ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <X className="h-3 w-3 text-muted-foreground" />
          )}
          <span className={strength.checks.hasNumber ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
            En siffra
          </span>
        </div>

        <div className="flex items-center gap-1 sm:col-span-2">
          {strength.checks.hasSpecialChar ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <X className="h-3 w-3 text-muted-foreground" />
          )}
          <span className={strength.checks.hasSpecialChar ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
            Ett specialtecken (!@#$%^&*)
          </span>
        </div>
      </div>
    </div>
  );
};
